import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function ImageCropper({ open, onClose, onCropComplete, lang = 'en' }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 75, aspect: 3 / 4 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const fileRef = useRef(null);

  const compressImage = (file, maxSizeMB = 4) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          let quality = 0.9;
          let maxDim = 2000;
          const tryCompress = () => {
            const canvas = document.createElement('canvas');
            let w = img.width, h = img.height;
            if (w > maxDim || h > maxDim) {
              const ratio = Math.min(maxDim / w, maxDim / h);
              w = Math.round(w * ratio);
              h = Math.round(h * ratio);
            }
            canvas.width = w;
            canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            const result = canvas.toDataURL('image/jpeg', quality);
            const sizeBytes = Math.round((result.length - 22) * 3 / 4);
            if (sizeBytes > maxSizeMB * 1024 * 1024 && quality > 0.3) {
              quality -= 0.15;
              maxDim = Math.round(maxDim * 0.8);
              tryCompress();
            } else {
              resolve(result);
            }
          };
          tryCompress();
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const onSelectFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      const compressed = await compressImage(file);
      setSrc(compressed);
    } else {
      const reader = new FileReader();
      reader.onload = () => setSrc(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const onImageLoaded = useCallback((e) => {
    imgRef.current = e.currentTarget;
  }, []);

  const getCroppedImg = () => {
    if (!imgRef.current || !completedCrop) return;
    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = 600;
    canvas.height = 800;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0, 0, 600, 800
    );
    const base64 = canvas.toDataURL('image/jpeg', 0.85);
    onCropComplete(base64);
    handleClose();
  };

  const handleClose = () => {
    setSrc(null);
    setCrop({ unit: '%', width: 75, aspect: 3 / 4 });
    setCompletedCrop(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="glass-card t-text max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="t-text">{lang === 'en' ? 'Crop Photo' : 'Potong Foto'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {!src ? (
            <div className="text-center py-8">
              <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onSelectFile} />
              <Button onClick={() => fileRef.current?.click()} className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full px-8">
                {lang === 'en' ? 'Choose Photo' : 'Pilih Foto'}
              </Button>
              <p className="t-text-muted text-xs mt-3">{lang === 'en' ? 'Large photos are auto-compressed. Drag to crop.' : 'Foto besar otomatis dikompres. Geser untuk memotong.'}</p>
            </div>
          ) : (
            <>
              <div className="rounded-xl overflow-hidden" style={{ maxHeight: '50vh' }}>
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={3 / 4}
                >
                  <img src={src} alt="Crop" onLoad={onImageLoaded} style={{ maxHeight: '50vh', width: '100%', objectFit: 'contain' }} />
                </ReactCrop>
              </div>
              <p className="t-text-muted text-xs text-center">{lang === 'en' ? 'Drag to adjust the crop area (3:4 ratio)' : 'Geser untuk menyesuaikan area potong (rasio 3:4)'}</p>
              <div className="flex gap-3">
                <Button onClick={() => setSrc(null)} variant="outline" className="flex-1 t-border t-text-secondary rounded-lg">
                  {lang === 'en' ? 'Choose Another' : 'Pilih Lain'}
                </Button>
                <Button onClick={getCroppedImg} disabled={!completedCrop} className="flex-1 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-lg disabled:opacity-50">
                  {lang === 'en' ? 'Apply Crop' : 'Terapkan'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
